/**
 * Enhanced Flood Data Generator
 * Generates comprehensive fake data for Vietnam Flood Monitoring System
 */

import * as turf from '@turf/turf';
import type { Polygon } from 'geojson';

import {
  SEVERITY_LABELS,
  type EnhancedFloodPoint,
  type Alert,
  type AlertType,
  type AlertSeverity,
  type SafeRoute,
  type EvacuationZone,
  type RescueStation,
  type FloodReport,
  type ReportType,
  type FloodCause,
  type WaterLevelForecast,
  type FloodHistoryRecord,
  type BlockedRoad,
  type HistoricalAnalysis,
  type DashboardStats,
  type WeatherSnapshot,
  type AffectedArea,
  type ReportTemplate,
  type TrendDirection,
  type AffectedAreaReport,
  type AoiFloodContext,
  type AoiLandCoverCategoryKey,
  type AoiLandCoverImpact,
} from '@/types/flood.types';

// ============================================================================
// Seeded Random Number Generator
// ============================================================================

function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ============================================================================
// Constants & Data
// ============================================================================

const HANOI_DISTRICTS = [
  {
    name: 'Đống Đa',
    wards: ['Quang Trung', 'Trung Liệt', 'Khâm Thiên', 'Thái Hà'],
    lat: 21.0116,
    lng: 105.8285,
  },
  {
    name: 'Thanh Xuân',
    wards: ['Nhân Chính', 'Thượng Đình', 'Kim Giang'],
    lat: 20.9986,
    lng: 105.815,
  },
  { name: 'Cầu Giấy', wards: ['Dịch Vọng', 'Trung Hòa', 'Yên Hòa'], lat: 21.0333, lng: 105.79 },
  {
    name: 'Hoàng Mai',
    wards: ['Định Công', 'Thịnh Liệt', 'Hoàng Liệt'],
    lat: 20.9813,
    lng: 105.8433,
  },
  {
    name: 'Hai Bà Trưng',
    wards: ['Bạch Mai', 'Trần Hưng Đạo', 'Ngô Thì Nhậm'],
    lat: 21.0111,
    lng: 105.8519,
  },
  { name: 'Ba Đình', wards: ['Trúc Bạch', 'Vĩnh Phúc', 'Cống Vị'], lat: 21.035, lng: 105.823 },
  { name: 'Tây Hồ', wards: ['Phú Thượng', 'Nhật Tân', 'Tứ Liên'], lat: 21.0633, lng: 105.8167 },
  { name: 'Hoàn Kiếm', wards: ['Hàng Bạc', 'Lý Thái Tổ', 'Hàng Đào'], lat: 21.0285, lng: 105.8542 },
];

const STREET_NAMES = [
  'Nguyễn Trãi',
  'Trần Duy Hưng',
  'Láng Hạ',
  'Đê La Thành',
  'Tây Sơn',
  'Thái Hà',
  'Chùa Bộc',
  'Xuân Thủy',
  'Cầu Giấy',
  'Hoàng Quốc Việt',
  'Nguyễn Khuyến',
  'Tôn Đức Thắng',
  'Phạm Văn Đồng',
  'Nguyễn Phong Sắc',
  'Dương Quảng Hàm',
  'Trung Kính',
  'Yên Hòa',
  'Phan Chu Trinh',
];

const CAUSES: FloodCause[] = [
  'heavy_rain',
  'high_tide',
  'blocked_drain',
  'combined',
  'dam_failure',
];
const CAUSE_DESCRIPTIONS: Record<FloodCause, string> = {
  heavy_rain:
    'Mưa lớn kéo dài với cường độ cao, gây ra lượng nước vượt khả năng thoát của hệ thống',
  high_tide: 'Triều cường dâng cao kết hợp với nước sông dâng cao, ngăn chặn dòng chảy tự nhiên',
  blocked_drain: 'Hệ thống cống thoát nước bị tắc nghẽn bởi rác thải và bùn đất',
  combined: 'Kết hợp của mưa lớn và triều cường, tạo ra áp lực vượt ngưỡng thiết kế',
  dam_failure: 'Sự cố tại đập thủy điện hoặc hồ chứa gây xả lũ đột ngột',
  dam_break: 'Đập bị vỡ hoàn toàn, giải phóng lượng nước lớn',
  unknown: 'Nguyên nhân đang được điều tra',
};

const IMMEDIATE_ACTIONS = [
  'Di chuyển ngay đến nơi cao hơn',
  'Không đi qua các tuyến đường bị ngập',
  'Gọi 112 nếu cần hỗ trợ khẩn cấp',
  'Theo dõi thông tin cảnh báo liên tục',
  'Tắt nguồn điện nếu nước bắt đầu vào nhà',
  'Gom góp đồ dùng cần thiết',
];

const SHORT_TERM_ACTIONS = [
  'Kiểm tra tình trạng nhà cửa sau khi nước rút',
  'Kiểm tra nguồn nước ăn uống',
  'Thông báo cho chính quyền địa phương về thiệt hại',
  'Chụp ảnh lưu lại thiệt hại để làm thủ tục bồi thường',
  'Theo dõi sức khỏe, đặc biệt trẻ em và người già',
];

// ============================================================================
// Helper Functions
// ============================================================================

function randomItem<T>(arr: T[], rand: () => number): T {
  return arr[Math.floor(rand() * arr.length)];
}

function randomBetween(min: number, max: number, rand: () => number): number {
  return min + rand() * (max - min);
}

function formatDuration(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} phút`;
  if (hours < 24) return `${Math.round(hours)} giờ ${Math.round((hours % 1) * 60)} phút`;
  return `${Math.floor(hours / 24)} ngày ${Math.round(hours % 24)} giờ`;
}

function getRiskLevelFromDepth(depthCm: number): AlertSeverity {
  if (depthCm >= 150) return 5;
  if (depthCm >= 80) return 4;
  if (depthCm >= 50) return 3;
  if (depthCm >= 30) return 2;
  return 1;
}

function getSeverityLabel(level: AlertSeverity): string {
  return SEVERITY_LABELS[level];
}

function generatePosition(
  base: { lat: number; lng: number },
  spread: number,
  rand: () => number,
): [number, number] {
  return [base.lng + (rand() - 0.5) * spread, base.lat + (rand() - 0.5) * spread];
}

function generatePhoneNumber(rand: () => number): string {
  const prefix = ['0243', '0244', '0214', '0234'][Math.floor(rand() * 4)];
  const suffix = Math.floor(rand() * 9000000 + 1000000);
  return `${prefix}.${suffix}`;
}

// ============================================================================
// Flood Point Generator
// ============================================================================

export function generateEnhancedFloodPoints(count: number = 15): EnhancedFloodPoint[] {
  const rand = seededRandom(42);
  const points: EnhancedFloodPoint[] = [];

  for (let i = 0; i < count; i++) {
    const district = randomItem(HANOI_DISTRICTS, rand);
    const ward = randomItem(district.wards, rand);
    const street = randomItem(STREET_NAMES, rand);
    const cause = randomItem(CAUSES, rand);
    const depthCm = Math.floor(randomBetween(20, 180, rand));
    const riskLevel = getRiskLevelFromDepth(depthCm);
    const population = Math.floor(randomBetween(5000, 50000, rand));

    const startTime = new Date(Date.now() - Math.floor(rand() * 8 * 60 * 60 * 1000)).toISOString();
    const durationHours = randomBetween(1, 12, rand);
    const expectedEndTime = new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString();

    const blockedRoads: BlockedRoad[] = [];
    const numBlockedRoads = Math.floor(rand() * 3) + 1;
    for (let j = 0; j < numBlockedRoads; j++) {
      blockedRoads.push({
        id: `road-${i}-${j}`,
        name: `Đường ${randomItem(STREET_NAMES, rand)}`,
        lengthM: Math.floor(randomBetween(200, 2000, rand)),
        severity: rand() > 0.5 ? 'blocked' : 'congested',
        alternateRoute: `Đường ${randomItem(STREET_NAMES, rand)}`,
        estimatedDelay: `${Math.floor(randomBetween(5, 30, rand))} phút`,
      });
    }

    const forecast: WaterLevelForecast[] = [];
    for (let h = 0; h < 6; h++) {
      const trend: TrendDirection = h < 2 ? 'rising' : h < 4 ? 'stable' : 'falling';
      forecast.push({
        time: new Date(Date.now() + h * 2 * 60 * 60 * 1000).toISOString(),
        predictedDepthCm: Math.max(
          0,
          depthCm + (trend === 'rising' ? 10 * h : trend === 'falling' ? -15 * h : 0),
        ),
        confidence: randomBetween(75, 95, rand),
        trend,
      });
    }

    const history: FloodHistoryRecord[] = [];
    for (let y = 2020; y <= 2025; y++) {
      for (let m = 1; m <= 12; m++) {
        if (rand() > 0.85) {
          history.push({
            id: `hist-${i}-${y}-${m}`,
            date: `${y}-${String(m).padStart(2, '0')}-${String(Math.floor(rand() * 28) + 1).padStart(2, '0')}`,
            maxDepthCm: Math.floor(randomBetween(20, 150, rand)),
            duration: formatDuration(randomBetween(2, 24, rand)),
            cause: randomItem(CAUSES, rand),
            damageLevel: rand() > 0.7 ? 'severe' : rand() > 0.4 ? 'moderate' : 'minor',
          });
        }
      }
    }

    const point: EnhancedFloodPoint = {
      id: `flood-${String(i + 1).padStart(3, '0')}`,
      name: `Ngã tư ${street} - ${district.name}`,
      position: generatePosition(district, 0.01, rand),
      depthCm,
      maxDepthCm: depthCm + Math.floor(randomBetween(5, 30, rand)),
      areaKm2: parseFloat(randomBetween(0.5, 5, rand).toFixed(2)),
      volumeM3: Math.floor(randomBetween(5000, 50000, rand)),

      startTime,
      expectedEndTime,
      duration: formatDuration(durationHours),
      lastUpdated: new Date(Date.now() - Math.floor(rand() * 30 * 60 * 1000)).toISOString(),

      riskLevel,
      severityLabel: getSeverityLabel(riskLevel),
      status: depthCm > 50 ? 'active' : 'monitoring',

      cause,
      causeDescription: CAUSE_DESCRIPTIONS[cause],

      province: 'Hà Nội',
      district: district.name,
      ward,
      address: `${Math.floor(rand() * 200 + 1)} ${street}, P.${ward}, Q.${district.name}`,

      population,
      households: Math.floor(population / 3.5),
      vulnerableGroups: {
        elderly: Math.floor(population * randomBetween(0.1, 0.2, rand)),
        children: Math.floor(population * randomBetween(0.05, 0.15, rand)),
        disabled: Math.floor(population * randomBetween(0.01, 0.05, rand)),
        pregnant: Math.floor(population * randomBetween(0.01, 0.03, rand)),
      },

      nearbyInfrastructure: {
        hospitals: Math.floor(rand() * 2) + 1,
        schools: Math.floor(rand() * 5) + 1,
        emergencyShelters: Math.floor(rand() * 3) + 1,
        fireStations: Math.floor(rand() * 2),
        policeStations: Math.floor(rand() * 2) + 1,
        evacuationCenters: Math.floor(rand() * 4) + 2,
      },

      blockedRoads,
      trafficStatus: blockedRoads.some((r) => r.severity === 'blocked') ? 'blocked' : 'congested',

      forecast,
      history: history.slice(-20),

      damageAssessment: {
        affectedHouses: Math.floor(rand() * 500) + 50,
        damagedHouses: Math.floor(rand() * 50) + 5,
        destroyedHouses: Math.floor(rand() * 5),
        economicLossUSD: Math.floor(randomBetween(100000, 5000000, rand)),
        agriculturalAreaHa: parseFloat(randomBetween(0, 50, rand).toFixed(2)),
      },

      safetyRecommendations: {
        immediate: IMMEDIATE_ACTIONS.slice(0, Math.floor(rand() * 3) + 2),
        shortTerm: SHORT_TERM_ACTIONS.slice(0, Math.floor(rand() * 3) + 1),
        evacuationRoutes: [
          'Tuyến 1: Hướng Bắc qua đường ${street} → Trường THPT ${district.name}',
          'Tuyến 2: Hướng Đông qua đường ${street} → Nhà văn hóa phường',
        ],
        emergencyContacts: [
          {
            name: 'Trung tâm chỉ huy Q.${district.name}',
            role: 'Địa phương',
            phone: generatePhoneNumber(rand),
            available: true,
          },
          {
            name: 'BV ${district.name}',
            role: 'Y tế',
            phone: generatePhoneNumber(rand),
            available: rand() > 0.2,
          },
        ],
      },

      photoUrl: '/camera_snapshot.svg',
      confidence: randomBetween(80, 98, rand),
      dataSource: 'GEE AlphaEarth · cảm biến IoT (Viện CKHTĐ)',
      verified: rand() > 0.1,
    };

    points.push(point);
  }

  return points;
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function combineDemoSeed(
  id: string,
  referenceDateIso?: string | null,
  scenarioYear?: number,
): number {
  return hashString(`${id}|${referenceDateIso ?? ''}|${scenarioYear ?? ''}`);
}

function parseReferenceDate(iso: string | null | undefined): Date | null {
  if (iso == null || String(iso).trim() === '') return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatViDate(d: Date): string {
  return d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const AOI_MODEL_LABELS = ['Phát hiện tự động trên ảnh', 'Phân tích ảnh (AI)'] as const;

const LAND_KEYS_FOR_SPLIT: AoiLandCoverCategoryKey[] = [
  'ricePaddy',
  'urbanBuilt',
  'forest',
  'perennialCrop',
  'surfaceWater',
];

/** Tóm tắt bề mặt trước sự kiện — văn bản trung tính cho popup (sinh có seed). */
const PRE_EVENT_SURFACE_SUMMARIES = [
  'Khu vực đô thị mật độ trung bình, nhiều mái cứng và bề mặt giao thông ít thấm; nước dễ đọng tại hè phố và khu trũng cục bộ.',
  'Chủ yếu đất lúa và mạng lưới mương nhỏ, địa hình trũng nhẹ so với dải dân cư liền kề.',
  'Vùng giao thoa đô thị–nông thôn: dãy nhà thấp tầng xen cây lâu năm, thoát nước bề mặt không đồng đều.',
  'Bề mặt nhiều bê tông và khu chợ cố định; một số đoạn cống thoát hẹp, rủi ro ứ đọng khi mưa lớn kéo dài.',
  'Vùng ven sông với bãi bồi thấp, thảm thực vật thưa; biên dao động nước nhạy với triều và mưa thượng nguồn.',
] as const;

const NEAREST_ASSISTANCE_LABEL_SUFFIXES = [
  'Trạm cứu hộ — {unit}',
  'Trung tâm chỉ huy — {unit}',
  'Trạm y tế khu vực — {unit}',
  'UBND — {unit}',
  'Điểm tập kết an toàn — {unit}',
] as const;

function buildExportFallbackPolygon(position: [number, number], areaKm2: number): Polygon {
  const km2 = Math.max(areaKm2, 0.01);
  const radiusKm = Math.sqrt(km2 / Math.PI);
  const f = turf.circle(position, radiusKm, { units: 'kilometers', steps: 64 });
  if (f.geometry.type !== 'Polygon') {
    throw new Error('turf.circle: expected Polygon');
  }
  return f.geometry;
}

function buildAoiFloodContext(params: {
  rand: () => number;
  depthCm: number;
  areaKm2: number;
  districtLabel: string;
  provinceLabel: string;
  referenceDateIso?: string | null;
}): AoiFloodContext {
  const { rand, depthCm, areaKm2, districtLabel, provinceLabel, referenceDateIso } = params;
  const refParsed = parseReferenceDate(referenceDateIso);
  const crisis = refParsed ?? new Date();
  const baselineEnd = new Date(crisis);
  baselineEnd.setUTCDate(baselineEnd.getUTCDate() - Math.floor(randomBetween(10, 22, rand)));
  const baselineStart = new Date(baselineEnd);
  baselineStart.setUTCDate(baselineStart.getUTCDate() - Math.floor(randomBetween(25, 42, rand)));

  const depthM = depthCm / 100;
  const minM = Math.max(0.05, depthM * randomBetween(0.55, 0.85, rand));
  const maxM = Math.max(minM + 0.12, depthM * randomBetween(1.05, 1.42, rand));

  const floodedAreaHa = Math.round(areaKm2 * 100 * 100) / 100;
  const affectedAreaPercent = Math.round(randomBetween(3.2, 31, rand) * 10) / 10;

  const weights = LAND_KEYS_FOR_SPLIT.map(() => rand());
  const wSum = weights.reduce((a, b) => a + b, 0) || 1;
  const landCoverImpacts: AoiLandCoverImpact[] = LAND_KEYS_FOR_SPLIT.map((categoryKey, i) => ({
    categoryKey,
    areaHa: Math.round(floodedAreaHa * (weights[i] / wSum) * 100) / 100,
  })).filter((x) => x.areaHa > 0.01);

  const analysisModelLabel = randomItem([...AOI_MODEL_LABELS], rand);

  const adminRef = [districtLabel, provinceLabel].filter((s) => s.trim().length > 0).join(', ');
  const preEventSurfaceSummary = randomItem([...PRE_EVENT_SURFACE_SUMMARIES], rand);
  const populationReferenceNote = adminRef
    ? `Ước tính trong AOI — tham chiếu WorldPop (lưới ~100 m), đơn vị: ${adminRef}.`
    : 'Ước tính trong AOI — tham chiếu WorldPop (lưới ~100 m).';

  const assistTpl = randomItem([...NEAREST_ASSISTANCE_LABEL_SUFFIXES], rand);
  const unitLabel = districtLabel.trim() || provinceLabel.trim() || 'địa phương';
  const nearestLabel = assistTpl.replace('{unit}', unitLabel);
  const distanceKm = Math.round(randomBetween(0.85, 11.5, rand) * 10) / 10;
  const etaMinutes =
    rand() > 0.35 ? Math.max(3, Math.floor(randomBetween(5, 38, rand))) : undefined;

  return {
    floodedAreaHa,
    affectedAreaPercent,
    administrativeUnitLabel: districtLabel.trim() || '—',
    estimatedDepthMinM: Math.round(minM * 100) / 100,
    estimatedDepthMaxM: Math.round(maxM * 100) / 100,
    landCoverImpacts,
    baselinePeriodLabel: `${formatViDate(baselineStart)} – ${formatViDate(baselineEnd)}`,
    crisisSceneLabel: formatViDate(crisis),
    analysisModelLabel,
    previewBeforeUrl: '/camera_snapshot.svg',
    previewAfterUrl: '/map-icons/flood-point.svg',
    preEventSurfaceSummary,
    populationReferenceNote,
    nearestAssistance: { label: nearestLabel, distanceKm, etaMinutes },
  };
}

export interface BuildEnhancedFloodPointFromDemoRegionInput {
  id: string;
  name: string;
  position: [number, number];
  depthCm: number;
  areaKm2: number;
  population: number;
  provinceLabel: string;
  districtLabel: string;
  /** Năm phân tích — gắn nhãn nguồn tham chiếu thống kê. */
  scenarioYear?: number;
  referenceDateIso?: string | null;
  /** Polygon AOI thật (bản đồ / SAR); nếu thiếu sẽ dùng vòng tròn xấp xỉ theo diện tích. */
  exportPolygon?: Polygon;
}

/**
 * Tạo một EnhancedFloodPoint đầy đủ trường từ polygon vùng GeoJSON,
 * dùng seed theo `id` để popup/forecast ổn định.
 */
export function buildEnhancedFloodPointFromDemoRegion(
  input: BuildEnhancedFloodPointFromDemoRegionInput,
): EnhancedFloodPoint {
  const rand = seededRandom(combineDemoSeed(input.id, input.referenceDateIso, input.scenarioYear));
  const depthCm = Math.max(0, Math.floor(input.depthCm));
  const riskLevel = getRiskLevelFromDepth(depthCm);
  const cause = randomItem(CAUSES, rand);
  const ward = randomItem(HANOI_DISTRICTS, rand).wards[0];
  const street = randomItem(STREET_NAMES, rand);
  const startTime = new Date(Date.now() - Math.floor(rand() * 8 * 60 * 60 * 1000)).toISOString();
  const durationHours = randomBetween(1, 12, rand);
  const expectedEndTime = new Date(Date.now() + durationHours * 60 * 60 * 1000).toISOString();

  const blockedRoads: BlockedRoad[] = [];
  const numBlockedRoads = Math.floor(rand() * 2) + 1;
  for (let j = 0; j < numBlockedRoads; j++) {
    blockedRoads.push({
      id: `road-${input.id}-${j}`,
      name: `Đường ${randomItem(STREET_NAMES, rand)}`,
      lengthM: Math.floor(randomBetween(200, 2000, rand)),
      severity: rand() > 0.5 ? 'blocked' : 'congested',
      alternateRoute: `Đường ${randomItem(STREET_NAMES, rand)}`,
      estimatedDelay: `${Math.floor(randomBetween(5, 30, rand))} phút`,
    });
  }

  const forecast: WaterLevelForecast[] = [];
  for (let h = 0; h < 6; h++) {
    const trend: TrendDirection = h < 2 ? 'rising' : h < 4 ? 'stable' : 'falling';
    forecast.push({
      time: new Date(Date.now() + h * 2 * 60 * 60 * 1000).toISOString(),
      predictedDepthCm: Math.max(
        0,
        depthCm + (trend === 'rising' ? 10 * h : trend === 'falling' ? -15 * h : 0),
      ),
      confidence: randomBetween(75, 95, rand),
      trend,
    });
  }

  const history: FloodHistoryRecord[] = [];
  for (let y = 2020; y <= 2025; y++) {
    for (let m = 1; m <= 12; m++) {
      if (rand() > 0.88) {
        history.push({
          id: `hist-${input.id}-${y}-${m}`,
          date: `${y}-${String(m).padStart(2, '0')}-${String(Math.floor(rand() * 28) + 1).padStart(2, '0')}`,
          maxDepthCm: Math.floor(randomBetween(20, Math.max(21, depthCm), rand)),
          duration: formatDuration(randomBetween(2, 24, rand)),
          cause: randomItem(CAUSES, rand),
          damageLevel: rand() > 0.7 ? 'severe' : rand() > 0.4 ? 'moderate' : 'minor',
        });
      }
    }
  }

  const areaKm2Rounded = Math.round(input.areaKm2 * 100) / 100;
  const aoiContext = buildAoiFloodContext({
    rand,
    depthCm,
    areaKm2: areaKm2Rounded,
    districtLabel: input.districtLabel,
    provinceLabel: input.provinceLabel,
    referenceDateIso: input.referenceDateIso,
  });

  const exportGeometry =
    input.exportPolygon ?? buildExportFallbackPolygon(input.position, areaKm2Rounded);

  const dataSourceLine =
    [input.districtLabel, input.provinceLabel].filter((s) => s?.trim()).join(', ') || '—';

  return {
    id: input.id,
    name: input.name,
    position: input.position,
    depthCm,
    maxDepthCm: depthCm + Math.floor(randomBetween(5, 30, rand)),
    areaKm2: areaKm2Rounded,
    volumeM3: Math.floor(randomBetween(5000, 50000, rand)),

    startTime,
    expectedEndTime,
    duration: formatDuration(durationHours),
    lastUpdated: new Date(Date.now() - Math.floor(rand() * 30 * 60 * 1000)).toISOString(),

    riskLevel,
    severityLabel: getSeverityLabel(riskLevel),
    status: depthCm > 50 ? 'active' : 'monitoring',

    cause,
    causeDescription: CAUSE_DESCRIPTIONS[cause],

    province: input.provinceLabel,
    district: input.districtLabel,
    ward,
    address: `${Math.floor(rand() * 200 + 1)} ${street}, P.${ward}, ${input.districtLabel}`,

    population: input.population,
    households: Math.floor(input.population / 3.5),
    vulnerableGroups: {
      elderly: Math.floor(input.population * randomBetween(0.1, 0.2, rand)),
      children: Math.floor(input.population * randomBetween(0.05, 0.15, rand)),
      disabled: Math.floor(input.population * randomBetween(0.01, 0.05, rand)),
      pregnant: Math.floor(input.population * randomBetween(0.01, 0.03, rand)),
    },

    nearbyInfrastructure: {
      hospitals: Math.floor(rand() * 2) + 1,
      schools: Math.floor(rand() * 5) + 1,
      emergencyShelters: Math.floor(rand() * 3) + 1,
      fireStations: Math.floor(rand() * 2),
      policeStations: Math.floor(rand() * 2) + 1,
      evacuationCenters: Math.floor(rand() * 4) + 2,
    },

    blockedRoads,
    trafficStatus: blockedRoads.some((r) => r.severity === 'blocked') ? 'blocked' : 'congested',

    forecast,
    history: history.slice(-20),

    damageAssessment: {
      affectedHouses: Math.floor(rand() * 500) + 50,
      damagedHouses: Math.floor(rand() * 50) + 5,
      destroyedHouses: Math.floor(rand() * 5),
      economicLossUSD: Math.floor(randomBetween(100000, 5000000, rand)),
      agriculturalAreaHa: parseFloat(randomBetween(0, 50, rand).toFixed(2)),
    },

    safetyRecommendations: {
      immediate: IMMEDIATE_ACTIONS.slice(0, Math.floor(rand() * 3) + 2),
      shortTerm: SHORT_TERM_ACTIONS.slice(0, Math.floor(rand() * 3) + 1),
      evacuationRoutes: [
        `Tuyến 1: Hướng Bắc qua đường ${street}`,
        `Tuyến 2: Hướng Đông qua đường ${street} → Nhà văn hóa`,
      ],
      emergencyContacts: [
        {
          name: `Trung tâm chỉ huy ${input.districtLabel}`,
          role: 'Địa phương',
          phone: generatePhoneNumber(rand),
          available: true,
        },
        {
          name: 'BV đa khoa khu vực',
          role: 'Y tế',
          phone: generatePhoneNumber(rand),
          available: rand() > 0.2,
        },
      ],
    },

    photoUrl: '/camera_snapshot.svg',
    satelliteImageryUrl: aoiContext.previewAfterUrl,
    confidence: randomBetween(80, 98, rand),
    dataSource: dataSourceLine,
    verified: rand() > 0.1,
    aoiContext,
    exportGeometry,
  };
}

export function generateFloodPointById(id: string): EnhancedFloodPoint {
  const points = generateEnhancedFloodPoints(15);
  return points.find((p) => p.id === id) || points[0];
}

// ============================================================================
// Alert Generator
// ============================================================================

const ALERT_TITLES: Record<AlertType, string[]> = {
  flood_warning: [
    'Cảnh báo ngập nghiêm trọng',
    'Ngập lụt tại khu vực trung tâm',
    'Mực nước vượt ngưỡng',
  ],
  rainfall_alert: ['Mưa lớn cảnh báo', 'Dự báo mưa to khu vực phía Bắc', 'Mưa dông trên diện rộng'],
  tide_alert: ['Triều cường cảnh báo', 'Mực nước triều dâng cao', 'Cảnh báo triều cường vượt BĐ3'],
  infrastructure_failure: [
    'Sự cố đập thủy điện',
    'Hệ thống cống bị tắc',
    'Trạm bơm ngừng hoạt động',
  ],
  evacuation_order: [
    'Lệnh sơ tán khẩn cấp',
    'Yêu cầu di dời ngay lập tức',
    'Sơ tán dân cư vùng ngập',
  ],
  emergency_broadcast: [
    'Thông báo khẩn từ UBND',
    'Chỉ thị từ Ban chỉ huy PCTT',
    'Thông tin cập nhật lúc',
  ],
  weather_warning: [
    'Cảnh báo thời tiết nguy hiểm',
    'Dông lốc có thể xảy ra',
    'Gió mạnh trên vùng biển',
  ],
  landslide_warning: ['Cảnh báo sạt lở đất', 'Nguy cơ sạt lở tại vùng núi', 'Đất đá sụp đổ'],
};

export function generateAlerts(count: number = 10): Alert[] {
  const rand = seededRandom(Date.now());
  const alerts: Alert[] = [];
  const types: AlertType[] = Object.keys(ALERT_TITLES) as AlertType[];
  const points = generateEnhancedFloodPoints(count);

  for (let i = 0; i < count; i++) {
    const type = randomItem(types, rand);
    const severity: AlertSeverity = (Math.floor(rand() * 5) + 1) as AlertSeverity;
    const titles = ALERT_TITLES[type];

    const alert: Alert = {
      id: `alert-${String(i + 1).padStart(4, '0')}`,
      type,
      severity,
      title: randomItem(titles, rand),
      message: `Tình hình ngập lụt diễn biến phức tạp tại khu vực. Mực nước tiếp tục được theo dõi và cập nhật liên tục.`,

      position: points[i].position,
      affectedArea: `${points[i].district}, ${points[i].province}`,

      timestamp: new Date(Date.now() - Math.floor(rand() * 4 * 60 * 60 * 1000)).toISOString(),
      expiresAt: new Date(Date.now() + Math.floor(rand() * 12 * 60 * 60 * 1000)).toISOString(),
      isActive: rand() > 0.2,

      recommendedActions: IMMEDIATE_ACTIONS.slice(0, Math.floor(rand() * 3) + 1),
      links: [
        { label: 'Xem bản đồ', url: `/map?alert=${i}`, type: 'map' },
        { label: 'Chi tiết', url: `/alerts/${i}`, type: 'info' },
      ],

      status: rand() > 0.7 ? 'new' : rand() > 0.4 ? 'acknowledged' : 'resolved',

      source: ['Cảnh báo tự động', 'IoT Sensors', 'Địa phương', 'Trung tâm dự báo'][
        Math.floor(rand() * 4)
      ],
      verified: rand() > 0.15,
    };

    alerts.push(alert);
  }

  return alerts.sort((a, b) => {
    if (a.severity !== b.severity) return b.severity - a.severity;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

// ============================================================================
// Safe Route Generator
// ============================================================================

export function generateSafeRoutes(from: [number, number], to: [number, number]): SafeRoute[] {
  const rand = seededRandom(from[0] * 1000 + to[0]);
  const routeCount = Math.floor(rand() * 2) + 2;
  const routes: SafeRoute[] = [];

  for (let i = 0; i < routeCount; i++) {
    const waypoints: [number, number][] = [from];
    const numWaypoints = Math.floor(rand() * 3) + 2;

    for (let w = 0; w < numWaypoints; w++) {
      waypoints.push([
        from[0] + (to[0] - from[0]) * ((w + 1) / (numWaypoints + 1)) + (rand() - 0.5) * 0.01,
        from[1] + (to[1] - from[1]) * ((w + 1) / (numWaypoints + 1)) + (rand() - 0.5) * 0.01,
      ]);
    }
    waypoints.push(to);

    const floodRiskPoints: {
      position: [number, number];
      riskLevel: 1 | 2 | 3;
      description: string;
    }[] = [];
    if (rand() > 0.5) {
      const numRiskPoints = Math.floor(rand() * 3) + 1;
      for (let r = 0; r < numRiskPoints; r++) {
        floodRiskPoints.push({
          position: waypoints[Math.floor(rand() * waypoints.length)],
          riskLevel: (Math.floor(rand() * 3) + 1) as 1 | 2 | 3,
          description: 'Có thể ngập nhẹ khi mưa lớn',
        });
      }
    }

    const instructions = [];
    for (let s = 0; s < waypoints.length - 1; s++) {
      instructions.push({
        step: s + 1,
        instruction: ['Rẽ phải', 'Rẽ trái', 'Đi thẳng', 'Quay đầu', 'Đi qua ngã tư'][
          Math.floor(rand() * 5)
        ],
        distance: `${randomBetween(0.3, 2, rand).toFixed(1)} km`,
        landmark: `Gần ${randomItem(STREET_NAMES, rand)}`,
      });
    }

    routes.push({
      id: `route-${i}`,
      startPosition: from,
      endPosition: to,
      waypoints,
      distanceKm: parseFloat(randomBetween(2, 15, rand).toFixed(2)),
      estimatedTime: `${Math.floor(randomBetween(5, 45, rand))} phút`,
      safetyLevel: i === 0 ? 'safe' : rand() > 0.5 ? 'caution' : 'avoid',
      floodRiskPoints,
      alternatives:
        routes.length > 1
          ? []
          : [
              {
                id: `alt-${i}-1`,
                distanceKm: parseFloat(randomBetween(3, 18, rand).toFixed(2)),
                estimatedTime: `${Math.floor(randomBetween(8, 55, rand))} phút`,
                floodRisk: 'none',
              },
            ],
      instructions,
    });
  }

  return routes;
}

// ============================================================================
// Evacuation Zone Generator
// ============================================================================

export function generateEvacuationZones(province: string, count: number = 10): EvacuationZone[] {
  const rand = seededRandom(province.length);
  const zones: EvacuationZone[] = [];

  const zoneTypes = ['Trường học', 'Nhà văn hóa', 'Trung tâm thể thao', 'Chùa', 'Nhà thi đấu'];

  for (let i = 0; i < count; i++) {
    const district = randomItem(HANOI_DISTRICTS, rand);
    zones.push({
      id: `evac-${province.slice(0, 2)}-${i}`,
      name: `${randomItem(zoneTypes, rand)} ${district.name}`,
      position: generatePosition(district, 0.02, rand),
      capacity: Math.floor(randomBetween(200, 1000, rand)),
      currentOccupancy: Math.floor(randomBetween(0, 300, rand)),
      status: rand() > 0.8 ? 'filling' : rand() > 0.95 ? 'full' : 'available',
      amenities: ['Nước sạch', 'Thực phẩm', 'Y tế', 'Điện dự phòng'].slice(
        0,
        Math.floor(rand() * 3) + 1,
      ),
      accessRoutes: [
        `Từ trung tâm Q.${district.name} theo hướng Bắc 500m`,
        `Đường ${randomItem(STREET_NAMES, rand)}`,
      ],
    });
  }

  return zones;
}

// ============================================================================
// Rescue Station Generator
// ============================================================================

export function generateRescueStations(
  lat: number,
  lng: number,
  radiusKm: number = 10,
  count: number = 8,
): RescueStation[] {
  const rand = seededRandom(lat * 100 + lng);
  const stations: RescueStation[] = [];

  const stationTypes: RescueStation['type'][] = [
    'fire_station',
    'police',
    'hospital',
    'military',
    'volunteer',
  ];
  const stationNames: Record<RescueStation['type'], string[]> = {
    fire_station: [
      'PCCC Quận Đống Đa',
      'PCCC Quận Thanh Xuân',
      'PCCC Quận Cầu Giấy',
      'PCCC Quận Hai Bà Trưng',
    ],
    police: [
      'Công an Q.Đống Đa',
      'Công an Q.Thanh Xuân',
      'Công an Q.Cầu Giấy',
      'Công an Q.Hai Bà Trưng',
    ],
    hospital: ['BV Đống Đa', 'BV Xanh Pông', 'BV Hòa Bình', 'BV 108'],
    military: ['Bộ Tư lệnh Thủ đô', 'Trung đoàn 301', 'Tiểu đoàn 193'],
    volunteer: ['Đội CTXH Đại học X', 'Hội Chữ thập đỏ Hà Nội', 'Đội tình nguyện Y'],
  };

  for (let i = 0; i < count; i++) {
    const type = randomItem(stationTypes, rand);
    const capacity = Math.floor(randomBetween(20, 200, rand));

    stations.push({
      id: `station-${i}`,
      name: randomItem(stationNames[type], rand),
      type,
      position: generatePosition({ lat, lng }, radiusKm / 111, rand),
      status: rand() > 0.8 ? 'busy' : rand() > 0.95 ? 'unavailable' : 'available',
      capacity,
      currentLoad: Math.floor(randomBetween(0, capacity * 0.8, rand)),
      services: getServicesForType(type),
      contactPhone: generatePhoneNumber(rand),
      distanceFromFlood: parseFloat(randomBetween(1, radiusKm, rand).toFixed(1)),
    });
  }

  return stations.sort((a, b) => (a.distanceFromFlood || 0) - (b.distanceFromFlood || 0));
}

function getServicesForType(type: RescueStation['type']): string[] {
  const services: Record<RescueStation['type'], string[]> = {
    fire_station: ['Cứu hộ', 'PCCC', 'Cắt cửa', 'Bơm nước'],
    police: ['Bảo vệ', 'Điều tiết giao thông', 'Hỗ trợ sơ tán'],
    hospital: ['Sơ cứu', 'Y tế', 'Xe cấp cứu'],
    military: ['Cứu hộ nặng', 'Vận chuyển', 'Hỗ trợ hậu cần'],
    volunteer: ['Phân phát thực phẩm', 'Hỗ trợ di chuyển', 'Tình nguyện viên'],
  };
  return services[type];
}

// ============================================================================
// Dashboard & Stats Generators
// ============================================================================

export function generateDashboardStats(): DashboardStats {
  const rand = seededRandom(Date.now() / 1000000);
  return {
    alertCount: Math.floor(randomBetween(5, 20, rand)),
    sensorsOnline: Math.floor(randomBetween(220, 250, rand)),
    floodZones: Math.floor(randomBetween(3, 12, rand)),
    areaMonitored: Math.floor(randomBetween(800, 1500, rand)),
    peakLevel: parseFloat(randomBetween(8.5, 13.5, rand).toFixed(1)),
    rainfall24h: Math.floor(randomBetween(50, 200, rand)),
    affectedPopulation: Math.floor(randomBetween(20000, 100000, rand)),
    trend: rand() > 0.5 ? 'increasing' : rand() > 0.5 ? 'stable' : 'decreasing',
  };
}

export function generateWeatherSnapshot(year: number, hour: number): WeatherSnapshot {
  const rand = seededRandom(year * 1000 + hour);
  const isRainy = rand() > 0.4;

  return {
    year,
    hour,
    tempC: Math.floor(randomBetween(22, 35, rand)),
    windKmh: Math.floor(randomBetween(5, 35, rand)),
    rainfall24hMm: isRainy
      ? Math.floor(randomBetween(20, 150, rand))
      : Math.floor(randomBetween(0, 20, rand)),
    humidityPct: Math.floor(randomBetween(60, 98, rand)),
    trendTempC: parseFloat(randomBetween(-3, 3, rand).toFixed(1)),
    trendRainMm: parseFloat(randomBetween(-20, 20, rand).toFixed(1)),
    trendHumidityPct: parseFloat(randomBetween(-10, 10, rand).toFixed(1)),
  };
}

export function generateAffectedAreas(
  year: number,
  hour: number,
  count: number = 5,
): AffectedArea[] {
  const rand = seededRandom(year * 100 + hour);
  const areas: AffectedArea[] = [];

  for (let i = 0; i < count; i++) {
    const district = randomItem(HANOI_DISTRICTS, rand);
    const depthM = parseFloat(randomBetween(0.2, 2.5, rand).toFixed(2));

    areas.push({
      name: district.name,
      depthM,
      pop: Math.floor(randomBetween(5000, 50000, rand)),
      status: depthM > 1.5 ? 'severe' : 'warning',
    });
  }

  return areas.sort((a, b) => b.depthM - a.depthM);
}

// ============================================================================
// Historical Analysis
// ============================================================================

export function generateHistoricalAnalysis(year: number, month?: number): HistoricalAnalysis {
  const rand = seededRandom(year * 10000 + (month || 0));
  const events = [];

  for (let m = 1; m <= 12; m++) {
    if (!month || m === month) {
      const numEvents = Math.floor(rand() * 5) + 1;
      for (let e = 0; e < numEvents; e++) {
        const district = randomItem(HANOI_DISTRICTS, rand);
        events.push({
          date: `${year}-${String(m).padStart(2, '0')}-${String(Math.floor(rand() * 28) + 1).padStart(2, '0')}`,
          location: district.name,
          severity: (Math.floor(rand() * 5) + 1) as AlertSeverity,
          maxDepth: Math.floor(randomBetween(30, 180, rand)),
        });
      }
    }
  }

  const severityDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  events.forEach((e) => {
    severityDistribution[e.severity]++;
  });

  const monthlyDistribution: Record<string, number> = {};
  events.forEach((e) => {
    const m = e.date.split('-')[1];
    monthlyDistribution[m] = (monthlyDistribution[m] || 0) + 1;
  });

  return {
    year,
    month,
    totalEvents: events.length,
    totalAffectedPopulation: Math.floor(randomBetween(100000, 1000000, rand)),
    totalAffectedArea: parseFloat(randomBetween(50, 500, rand).toFixed(2)),
    severityDistribution,
    monthlyDistribution,
    peakWaterLevel: parseFloat(randomBetween(10, 15, rand).toFixed(1)),
    totalRainfall: Math.floor(randomBetween(2000, 4000, rand)),
    economicLoss: Math.floor(randomBetween(100, 2000, rand)),
    events,
  };
}

// ============================================================================
// Report Generator
// ============================================================================

export function generateReport(
  type: ReportType,
  period: { start: string; end: string },
): FloodReport {
  const rand = seededRandom(period.start.length + period.end.length);
  const stats = generateDashboardStats();

  return {
    id: `report-${Date.now()}`,
    type,
    title: getReportTitle(type),
    period: {
      startDate: period.start,
      endDate: period.end,
    },
    summary: {
      totalFloodPoints: stats.floodZones,
      totalAffectedPopulation: stats.affectedPopulation,
      totalAffectedArea: parseFloat(randomBetween(20, 200, rand).toFixed(2)),
      severityDistribution: { 1: 2, 2: 5, 3: 8, 4: 3, 5: 1 },
      trend: stats.trend,
    },
    statistics: {
      averageDepth: parseFloat(randomBetween(0.5, 2, rand).toFixed(2)),
      maxDepth: parseFloat(randomBetween(1.5, 3, rand).toFixed(2)),
      totalDuration: `${Math.floor(randomBetween(24, 168, rand))} giờ`,
      rainfallTotal: Math.floor(randomBetween(200, 600, rand)),
      peakWaterLevel: stats.peakLevel,
    },
    affectedAreas: generateAffectedAreas(
      new Date(period.start).getFullYear(),
      new Date(period.start).getMonth() + 1,
      5,
    ).map(
      (area): AffectedAreaReport => ({
        name: area.name,
        population: area.pop,
        area: randomBetween(1, 20, rand),
        avgDepth: area.depthM,
        maxDepth: area.depthM + randomBetween(0, 0.5, rand),
        status: area.status === 'severe' ? 'flooded' : 'receding',
      }),
    ),
    damageAssessment: {
      casualties: {
        deaths: Math.floor(rand() * 5),
        injured: Math.floor(rand() * 20),
        missing: Math.floor(rand() * 3),
        evacuated: Math.floor(rand() * 5000),
        rescued: Math.floor(rand() * 500),
      },
      property: {
        housesFlooded: Math.floor(randomBetween(500, 5000, rand)),
        housesDamaged: Math.floor(randomBetween(50, 500, rand)),
        housesDestroyed: Math.floor(randomBetween(5, 50, rand)),
        vehiclesAffected: Math.floor(randomBetween(20, 200, rand)),
      },
      economic: {
        totalUSD: Math.floor(randomBetween(1, 50, rand)),
        agricultural: Math.floor(randomBetween(0.5, 10, rand)),
        industrial: Math.floor(randomBetween(0.3, 15, rand)),
        infrastructure: Math.floor(randomBetween(0.2, 8, rand)),
        rescueCost: Math.floor(randomBetween(0.1, 2, rand)),
      },
      infrastructure: {
        roadsKm: parseFloat(randomBetween(10, 100, rand).toFixed(1)),
        bridges: Math.floor(randomBetween(2, 20, rand)),
        schools: Math.floor(randomBetween(5, 30, rand)),
        hospitals: Math.floor(randomBetween(1, 5, rand)),
      },
    },
    responseActions: [
      {
        time: '06:00',
        action: 'Kích hoạt phương án chống ngập',
        responsibleParty: 'UBND Q.Đống Đa',
        outcome: 'Đã triển khai lực lượng',
      },
      {
        time: '07:30',
        action: 'Cắt điện các khu vực nguy hiểm',
        responsibleParty: 'Điện lực Hà Nội',
        outcome: 'Hoàn thành an toàn',
      },
      {
        time: '08:00',
        action: 'Sơ tán dân vùng ngập sâu',
        responsibleParty: 'Quân đội + Công an',
        outcome: 'Đã sơ tán 1,500 hộ dân',
      },
    ],
    attachments: [],
    generatedAt: new Date().toISOString(),
    generatedBy: 'Hệ thống tự động',
  };
}

function getReportTitle(type: ReportType): string {
  const titles: Record<ReportType, string> = {
    situation_summary: 'Báo cáo Tình hình Ngập lụt Tổng hợp',
    detailed_analysis: 'Phân tích Chi tiết Đợt Ngập',
    damage_assessment: 'Đánh giá Thiệt hại',
    forecast: 'Báo cáo Dự báo',
    realtime: 'Báo cáo Cập nhật Thời gian thực',
    evacuation: 'Báo cáo Sơ tán và Phản ứng',
  };
  return titles[type];
}

export function getReportTemplates(): ReportTemplate[] {
  return [
    {
      id: 'template-1',
      name: 'Báo cáo tổng hợp',
      type: 'situation_summary',
      description: 'Báo cáo tổng hợp toàn diện về tình hình ngập lụt',
      sections: ['Tóm tắt', 'Thống kê', 'Khu vực ảnh hưởng', 'Thiệt hại', 'Hành động phản ứng'],
    },
    {
      id: 'template-2',
      name: 'Phân tích chi tiết',
      type: 'detailed_analysis',
      description: 'Phân tích sâu về nguyên nhân, diễn biến và dự báo',
      sections: ['Phân tích nguyên nhân', 'Diễn biến', 'Dự báo', 'Khuyến nghị'],
    },
    {
      id: 'template-3',
      name: 'Đánh giá thiệt hại',
      type: 'damage_assessment',
      description: 'Báo cáo chi tiết về thiệt hại về người và tài sản',
      sections: ['Thiệt hại về người', 'Thiệt hại tài sản', 'Thiệt hại kinh tế', 'Hạ tầng'],
    },
  ];
}

// ============================================================================
// Water Level Forecast
// ============================================================================

export function generateForecast(floodPointId: string, hours: number = 24): WaterLevelForecast[] {
  const rand = seededRandom(floodPointId.length * 1000);
  const forecasts: WaterLevelForecast[] = [];

  let currentDepth = randomBetween(50, 150, rand);
  let trend: TrendDirection;

  for (let h = 0; h < hours; h += 2) {
    if (h > 4 && h < 12) {
      trend = rand() > 0.6 ? 'rising' : 'stable';
    } else if (h >= 12) {
      trend = 'falling';
    } else {
      trend = 'stable';
    }

    const change =
      trend === 'rising'
        ? randomBetween(5, 15, rand)
        : trend === 'falling'
          ? randomBetween(-15, -5, rand)
          : randomBetween(-3, 3, rand);
    currentDepth = Math.max(0, currentDepth + change);

    forecasts.push({
      time: new Date(Date.now() + h * 60 * 60 * 1000).toISOString(),
      predictedDepthCm: Math.floor(currentDepth),
      confidence: randomBetween(75, 95, rand),
      trend,
    });
  }

  return forecasts;
}
